import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Home as HomeIcon, HardHat, Hammer, Building2, Truck, ShieldCheck, ArrowRight, Zap, MapPin, DollarSign, Activity, CheckCircle2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Section, Eyebrow, SectionTitle, currency } from "@/components/Primitives";
import api from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1527335988388-b40ee248d80c?crop=entropy&cs=srgb&fm=jpg&q=85";
const CREW = "https://images.unsplash.com/photo-1694521787673-28cbd8830ea5?crop=entropy&cs=srgb&fm=jpg&q=85";
const VAN = "https://images.unsplash.com/photo-1620455800201-7f00aeef12ed?crop=entropy&cs=srgb&fm=jpg&q=85";

const ROLES = [
  { id: "homeowner", label: "Homeowner", icon: HomeIcon, blurb: "Post a job, hire verified pros, track every milestone." },
  { id: "contractor", label: "Contractor", icon: HardHat, blurb: "Win contracts near you. WSIB & insurance verified access." },
  { id: "laborer", label: "General Labor", icon: Hammer, blurb: "Find paid gigs and steady work on active job sites." },
  { id: "realtor", label: "Realtor / Investor", icon: Building2, blurb: "Buy, sell, rent or flip. Connect with deal-ready capital." },
  { id: "driver", label: "Drivers", icon: Truck, blurb: "Haul materials from Home Depot & Lowe's. Get paid per run." },
];

const STEPS = [
  { t: "Post or Browse", d: "Homeowners post jobs. Contractors, drivers and investors browse the live marketplace.", icon: Activity },
  { t: "Connect for a Fee", d: "A transparent connection fee links the right people instantly.", icon: Zap },
  { t: "Verify & Deliver", d: "Contractors verify WSIB + insurance. Drivers haul materials to site.", icon: ShieldCheck },
  { t: "Track to Payout", d: "Follow every step from Assigned to Payment Released with live tracking.", icon: CheckCircle2 },
];

const Home = () => {
  const [active, setActive] = useState("homeowner");
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [pros, setPros] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/jobs").then((r) => setJobs(r.data.slice(0, 4))).catch(() => {});
    api.get("/contractors/featured").then((r) => setPros(r.data)).catch(() => {});
  }, []);

  const current = ROLES.find((r) => r.id === active);

  return (
    <div>
      {/* Hero */}
      <div className="relative overflow-hidden eti-grid-bg border-b border-white/10">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0b0d10] via-[#0b0d10]/85 to-[#0b0d10]/60" />
        </div>
        <Section className="relative py-20 lg:py-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <Eyebrow>The Industrial Construction & Real Estate Marketplace</Eyebrow>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight uppercase leading-[1.05] text-white">
              One Platform.<br />Every Trade.<br /><span className="text-primary">One Connection Away.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-xl leading-relaxed">
              Exclusive Trades Inc connects homeowners with reputable contractors, laborers, realtors, investors and drivers — while tracking every job from request to payout. We charge a small, transparent fee for each successful connection, so you only pay when the right match is made.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => navigate("/marketplace")} className="bg-primary hover:bg-primary/90 text-white font-semibold h-12 px-6 text-base" data-testid="hero-explore-button">
                Explore Marketplace <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button onClick={() => navigate("/how-it-works")} variant="outline" className="border-white/20 text-white hover:bg-white/5 h-12 px-6 text-base" data-testid="hero-howitworks-button">
                How It Works
              </Button>
            </div>
          </motion.div>
        </Section>

        {/* Ticker */}
        {stats && (
          <div className="relative border-t border-white/10 bg-black/40 overflow-hidden">
            <div className="flex whitespace-nowrap eti-marquee py-3">
              {[...Array(2)].map((_, k) => (
                <div key={k} className="flex items-center gap-8 px-4">
                  {[
                    `${stats.open_jobs} open contracts`,
                    `${stats.active_deliveries} active delivery runs`,
                    `${stats.verified_contractors}+ verified contractors`,
                    `${stats.properties} live properties`,
                    `$${stats.payouts} in tracked payouts`,
                  ].map((s, i) => (
                    <span key={i} className="font-mono text-xs uppercase tracking-widest text-slate-400 flex items-center gap-8">
                      <span className="h-1.5 w-1.5 rounded-full bg-primary" />{s}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Role selector */}
      <Section className="py-16 lg:py-20">
        <Eyebrow>Built For Everyone On The Job</Eyebrow>
        <SectionTitle>Choose Your Lane</SectionTitle>
        <div className="mt-8 grid lg:grid-cols-5 gap-3">
          {ROLES.map((r) => (
            <button key={r.id} onClick={() => setActive(r.id)}
              className={`eti-card rounded-lg p-5 text-left ${active === r.id ? "!border-primary" : ""}`}
              data-testid={`hero-role-selector-${r.id}-button`}>
              <r.icon className={`h-7 w-7 mb-3 ${active === r.id ? "text-primary" : "text-slate-400"}`} />
              <p className="font-display font-bold text-white uppercase text-sm tracking-wide">{r.label}</p>
            </button>
          ))}
        </div>
        <motion.div key={active} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6 eti-card rounded-lg p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-md bg-primary/15 flex items-center justify-center"><current.icon className="h-6 w-6 text-primary" /></div>
            <div>
              <p className="font-display font-bold text-white uppercase">{current.label}</p>
              <p className="text-sm text-slate-400">{current.blurb}</p>
            </div>
          </div>
          <Button onClick={() => navigate("/pricing")} className="bg-primary hover:bg-primary/90 text-white font-semibold shrink-0" data-testid="role-get-started-button">Get Started</Button>
        </motion.div>
      </Section>

      {/* How it works bento */}
      <Section className="py-8 lg:py-12">
        <Eyebrow>The Four-Step Flow</Eyebrow>
        <SectionTitle>How Exclusive Trades Works</SectionTitle>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map((s, i) => (
            <div key={i} className="eti-card rounded-lg p-6" data-testid={`home-step-${i}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="font-mono text-3xl font-bold text-white/15">0{i + 1}</span>
                <s.icon className="h-6 w-6 text-primary" />
              </div>
              <h4 className="font-display font-bold text-white uppercase text-base tracking-wide">{s.t}</h4>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* Featured jobs */}
      <Section className="py-16 lg:py-20">
        <div className="flex items-end justify-between">
          <div><Eyebrow>Live Now</Eyebrow><SectionTitle>Open Contracts</SectionTitle></div>
          <Link to="/marketplace" className="text-primary text-sm font-semibold hidden sm:flex items-center gap-1" data-testid="home-view-all-jobs">View all <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {jobs.map((j) => (
            <div key={j.id} className="eti-card rounded-lg p-5 flex flex-col" data-testid={`home-job-${j.id}`}>
              <span className="eti-label">{j.trade}</span>
              <h4 className="font-display font-bold text-white mt-2 leading-tight">{j.title}</h4>
              <p className="text-xs text-slate-400 mt-2 flex items-center gap-1"><MapPin className="h-3 w-3" />{j.location}</p>
              <div className="mt-auto pt-4 flex items-center justify-between">
                <span className="font-mono text-primary font-bold flex items-center"><DollarSign className="h-3.5 w-3.5" />{currency(j.budget).replace("$", "")}</span>
                <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-white/5 text-slate-300">{j.urgency}</span>
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Top rated pros */}
      {pros.length > 0 && (
        <Section className="py-8">
          <Eyebrow>Top Rated</Eyebrow>
          <SectionTitle>Featured Verified Pros</SectionTitle>
          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pros.map((p) => (
              <Link key={p.user_id} to={`/profile/${p.user_id}`} className="eti-card rounded-lg p-5 flex items-center gap-4" data-testid={`home-pro-${p.user_id}`}>
                <div className="h-12 w-12 rounded-md bg-primary/15 flex items-center justify-center overflow-hidden shrink-0">
                  {p.avatar ? <img src={p.avatar} alt="" className="h-full w-full object-cover" /> : <HardHat className="h-6 w-6 text-primary" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-display font-bold text-white leading-tight truncate">{p.name}</p>
                  <p className="text-xs text-slate-400 truncate">{p.headline || p.company || "Verified Contractor"}</p>
                </div>
                <span className="flex items-center gap-1 text-primary text-sm font-mono shrink-0"><Star className="h-3.5 w-3.5 fill-primary" />{p.rating}</span>
              </Link>
            ))}
          </div>
        </Section>
      )}

      {/* Driver highlight */}
      <Section className="py-8">
        <div className="grid lg:grid-cols-2 gap-8 items-center eti-card rounded-2xl overflow-hidden">
          <div className="p-8 lg:p-12">
            <Eyebrow>Cargo & Delivery Drivers</Eyebrow>
            <SectionTitle>Skip The Supply Run</SectionTitle>
            <p className="mt-4 text-slate-300 leading-relaxed">
              Got a cargo van, Sprinter or SUV? Claim delivery runs from Home Depot, Lowe's and suppliers straight to the job site — saving contractors and homeowners hours on site during renovations. Get paid per run.
            </p>
            <Button onClick={() => navigate("/marketplace")} className="mt-6 bg-primary hover:bg-primary/90 text-white font-semibold" data-testid="home-driver-cta">
              See Delivery Board <Truck className="ml-2 h-4 w-4" />
            </Button>
          </div>
          <div className="h-64 lg:h-full min-h-[280px]"><img src={VAN} alt="Cargo van delivery" className="w-full h-full object-cover" /></div>
        </div>
      </Section>

      {/* Trust / fee */}
      <Section className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="h-72 lg:h-96 rounded-2xl overflow-hidden order-2 lg:order-1"><img src={CREW} alt="Contractor crew" className="w-full h-full object-cover" /></div>
          <div className="order-1 lg:order-2">
            <Eyebrow>Trust & Safety First</Eyebrow>
            <SectionTitle>Verified Pros. Transparent Fees.</SectionTitle>
            <div className="mt-6 space-y-4">
              {[
                ["WSIB & Insurance Verified", "Every contractor must submit WSIB and liability insurance before accessing customer job listings."],
                ["Connection Fee", "A small, transparent fee links the right people — no hidden markups, no surprises."],
                ["Milestone Progress Tracking", "Watch each job move from Requested to Payment Released, step by step."],
              ].map(([t, d], i) => (
                <div key={i} className="flex gap-3">
                  <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                  <div><p className="font-semibold text-white">{t}</p><p className="text-sm text-slate-400">{d}</p></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
};

export default Home;
